import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type {
  ApiClient, Bauteilbefund, Datenblattimport, Importpruefung, Zuordnungsliste, Zuordnungszeile,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import { elementkennung } from '../../elementkennung';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, Feld, KpiCard, KpiReihe, LoadingState, PropertyGrid,
  StatusBadge, formatiereZahl, spalteFuer, type Spalte,
} from '../../muster';
import { ZUORDNUNG_ZUSTAND, spanne, zuordnungZustand } from './datenregeln';
import { Zuordnen, type Vorgabe } from './DatenblattimporteZuordnen';
import { Ablagefeld } from './LueftungDatenblatt';
import './lueftung.css';

type Befundbauteil = Bauteilbefund['bauteile'][number];

/** Was gerade offen ist: die Zuordnung eines Dokuments, die Befunde eines Typs oder die Wahl des Dokuments. */
type Schubfach =
  | { art: 'zuordnen'; importId: string; vorgabe?: Vorgabe | null }
  | { art: 'befunde'; zeile: Zuordnungszeile }
  | { art: 'dokument'; zeile: Zuordnungszeile };

/**
 * Herstellerdaten als JSON einlesen.
 *
 * Fünf Schritte, absichtlich getrennt: Datei prüfen, Ziel festlegen, Merkmale
 * wählen, Vorschau, übernehmen - ein Herstellerwert ist nur mit seinen
 * Bedingungen gültig (`DatenblattimporteZuordnen.tsx`).
 *
 * TV6d (TX-STANDARD 1.6): nach `seiten/lueftung/` umgezogen (Route bleibt
 * `lueftung/importe`), mit Seitenkopf (vorher keiner), Kennzahlen und drei
 * `DataTable` statt fünf roher Tabellen. Die aufklappbaren Zeilen (Befunde,
 * Wahl des Dokuments) sind Schubfächer; die Zuordnung ebenso. Alle Aufrufe wie
 * vorher (`Lueftungsdaten.schreibwege.test.tsx`).
 */
export function Datenblattimporte() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { id } = useParams<{ id: string }>();

  const [importe, setImporte] = useState<Datenblattimport[] | null>(null);
  const [liste, setListe] = useState<Zuordnungsliste | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [listenfehler, setListenfehler] = useState<unknown>(null);
  const [offen, setOffen] = useState<Schubfach | null>(null);
  // Hochgezählt nach jeder Zuordnung, damit die Liste neu lädt.
  const [zaehler, setZaehler] = useState(0);
  const [nurOffen, setNurOffen] = useState(false);

  const laden = useCallback(async () => {
    try {
      setImporte(await api.importe());
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  }, [api]);

  useEffect(() => { void laden(); }, [laden]);

  useEffect(() => {
    api.importZuordnungen(projekt.id).then((l) => { setListe(l); setListenfehler(null); }).catch(setListenfehler);
  }, [api, projekt.id, zaehler]);

  const zeilen = useMemo(
    () => (liste ? (nurOffen ? liste.zeilen.filter((z) => z.zustand !== 'zugewiesen') : liste.zeilen) : []),
    [liste, nurOffen],
  );

  const zuordnungsspalten: Spalte<Zuordnungszeile>[] = useMemo(() => [
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', wert: (z) => ZUORDNUNG_ZUSTAND[z.zustand] ?? z.zustand,
      inhalt: (z) => (
        <span className="lue-marken">
          <StatusBadge klein zustand={zuordnungZustand(z.zustand)} text={ZUORDNUNG_ZUSTAND[z.zustand] ?? z.zustand} />
          {z.zugewiesen > 0 && z.zustand !== 'zugewiesen' && (
            <span className="lue-leise">{formatiereZahl(z.zugewiesen, 0)}/{formatiereZahl(z.bauteile, 0)}</span>
          )}
          {z.interpoliert > 0 && (
            <StatusBadge klein zustand="neutral" text={`${formatiereZahl(z.interpoliert, 0)} interp.`} titel="Zwischen zwei gedruckten Zeilen berechnet" />
          )}
        </span>
      ),
    },
    {
      schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (z) => z.bauteile,
      inhalt: (z) => (
        <>
          <span>{formatiereZahl(z.bauteile, 0)}</span>
          {z.mitLuftmenge < z.bauteile && <span className="lue-zweitzeile">{formatiereZahl(z.mitLuftmenge, 0)} mit Menge</span>}
        </>
      ),
    },
    { schluessel: 'familie', titel: 'Familie', wert: (z) => z.familie },
    {
      schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (z) => z.typ,
      inhalt: (z) => (
        <>
          <span>{z.typ}</span>
          {z.mengeVon !== null && <span className="lue-zweitzeile">{spanne(z.mengeVon, z.mengeBis)} m³/h</span>}
        </>
      ),
    },
    {
      schluessel: 'dokument', titel: 'Dokument', wert: (z) => (z.importSerie ? `${z.importHersteller} ${z.importSerie}` : null),
      // Ein Datenblatt aus dem PDF-Weg gehört nicht hierher - es zu verschweigen wäre aber schlimmer.
      inhalt: (z) => (z.importSerie ? (
        <span className="lue-marken">
          <span>{z.importHersteller} {z.importSerie}</span>
          {z.ausPdf && <StatusBadge klein zustand="neutral" text="aus PDF" />}
        </span>
      ) : null),
    },
    { schluessel: 'am', titel: 'zugewiesen am', wert: (z) => (z.zugewiesenAm ? new Date(z.zugewiesenAm) : null) },
    { schluessel: 'von', titel: 'von', wert: (z) => z.zugewiesenVon },
    {
      schluessel: 'aktionen', titel: '', sortierbar: false, breite: '280px', ausrichtung: 'rechts',
      inhalt: (z) => (
        <span className="lue-marken">
          {/* Bei einem Befund zuerst die Frage, was los ist - nicht, was man neu zuweist. */}
          {z.mitBefund > 0 && (
            <button type="button" className="m-knopf" onClick={() => setOffen({ art: 'befunde', zeile: z })}
              title={`${z.mitBefund} Bauteil(e) liegen außerhalb der Herstellertabelle`}>
              {formatiereZahl(z.mitBefund, 0)} Befund
            </button>
          )}
          {/* Aktualisieren nur mit einer Zuweisung aus JSON: Die gespeicherte Auswahl ist der Grund, warum es ohne
              Zusammenklicken geht. */}
          {z.importId && (
            <button type="button" className="m-knopf" title="Mit derselben Auswahl neu rechnen - für geänderte Luftmengen"
              onClick={() => setOffen({ art: 'zuordnen', importId: z.importId!, vorgabe: { familie: z.familie, typ: z.typ, auswahl: z.auswahl } })}>
              aktualisieren
            </button>
          )}
          <button type="button" className="m-knopf" onClick={() => setOffen({ art: 'dokument', zeile: z })}>
            {z.importId ? 'neu zuweisen' : 'zuweisen'}
          </button>
        </span>
      ),
    },
  ], []);

  const dokumentspalten: Spalte<Datenblattimport>[] = useMemo(() => [
    { schluessel: 'hersteller', titel: 'Hersteller', immerSichtbar: true, wert: (i) => i.hersteller },
    { schluessel: 'serie', titel: 'Serie', wert: (i) => i.serie },
    { schluessel: 'groessen', titel: 'Größen', art: 'zahl', nachkomma: 0, wert: (i) => i.anzahlGroessen },
    { schluessel: 'punkte', titel: 'Punkte', art: 'zahl', nachkomma: 0, wert: (i) => i.anzahlBetriebspunkte },
    { schluessel: 'verwendet', titel: 'verwendet', art: 'zahl', nachkomma: 0, wert: (i) => (i.verwendet > 0 ? i.verwendet : null) },
    {
      schluessel: 'quelle', titel: 'Quelle', wert: (i) => i.quellDateiname ?? i.dateiname,
      inhalt: (i) => (
        <span className="lue-marken">
          <span>{i.quellDateiname ?? i.dateiname}{i.quellSeiten ? ` · ${formatiereZahl(i.quellSeiten, 0)} S.` : ''}</span>
          {i.anzahlWarnungen > 0 && <StatusBadge klein zustand="warnung" text={`${formatiereZahl(i.anzahlWarnungen, 0)} Warnung(en)`} />}
        </span>
      ),
    },
    {
      schluessel: 'aktion', titel: '', sortierbar: false, ausrichtung: 'rechts',
      inhalt: (i) => <button type="button" className="m-knopf" onClick={() => setOffen({ art: 'zuordnen', importId: i.id })}>zuordnen</button>,
    },
  ], []);

  const zuTun = liste ? liste.jeZustand.filter((z) => z.zustand !== 'zugewiesen').reduce((n, z) => n + z.typen, 0) : 0;

  return (
    <>
      <PageHeader
        titel="Herstellerdaten einlesen"
        beschreibung={'JSON im Format tuv-datasheet-import/1.0.0. Anders als bei einem PDF bleiben Bedingungen und '
          + 'Vergleichsoperatoren erhalten - ein Wert „< 15 dB(A)“ ist keine 15.'}
        meta={<Link to={`/projekte/${id}/lueftung/datenblaetter`}>← Herstellerunterlagen</Link>}
      />

      {liste && (
        <KpiReihe>
          <KpiCard titel="Bauteile" wert={liste.bauteile} />
          <KpiCard titel="zugewiesen" wert={liste.zugewiesen} vergleich={`von ${formatiereZahl(liste.bauteile, 0)} Bauteilen`} />
          <KpiCard titel="Typen zu tun" wert={zuTun} vergleich={`von ${formatiereZahl(liste.zeilen.length, 0)} Typen`} />
          <KpiCard titel="Dokumente" wert={importe?.length ?? null} />
        </KpiReihe>
      )}

      <Hochladen api={api} neuLaden={() => void laden()} auswaehlen={(importId) => setOffen({ art: 'zuordnen', importId })} />

      <ContentSection
        titel="Zuordnungen je Bauteiltyp"
        beschreibung={liste && liste.jeZustand.length > 0
          ? liste.jeZustand.map((z) => `${ZUORDNUNG_ZUSTAND[z.zustand] ?? z.zustand}: ${formatiereZahl(z.typen, 0)} Typ(en), ${formatiereZahl(z.bauteile, 0)} Bauteil(e)`).join(' · ')
          : 'Sortiert nach Dringlichkeit: Ein Befund wiegt schwerer als eine geänderte Menge, die schwerer als eine fertige Zuweisung.'}
        nackt
      >
        {listenfehler !== null && <ErrorState klein meldung="Die Zuordnungen konnten nicht geladen werden." technisch={listenfehler} />}
        {!liste && listenfehler === null && <LoadingState text="Zuordnungen werden geladen …" zeilen={5} />}
        {liste && (
          <DataTable
            name="Zuordnungen je Bauteiltyp"
            spalten={zuordnungsspalten}
            zeilen={zeilen}
            schluessel={(z) => `${z.familie}|${z.typ}`}
            zeilenKlasse={(z) => (z.zustand === 'zugewiesen' ? 'zeile-zugewiesen' : undefined)}
            suchePlatzhalter="Familie oder Typ"
            werkzeuge={<Feld art="haken" beschriftung="Nur zeigen, was noch zu tun ist" wert={nurOffen} beiAenderung={(w) => setNurOffen(w as boolean)} />}
            leer={<EmptyState klein titel={nurOffen ? 'Alles zugewiesen.' : 'Keine Bauteiltypen im Projekt.'} />}
          />
        )}
      </ContentSection>

      {fehler !== null && <ErrorState meldung="Die eingelesenen Dokumente konnten nicht geladen werden." technisch={fehler} beiErneutVersuchen={() => void laden()} />}
      {importe && (
        <DataTable
          name="Eingelesene Dokumente"
          spalten={dokumentspalten}
          zeilen={importe}
          schluessel={(i) => i.id}
          leer={<EmptyState klein titel="Noch kein Herstellerdokument eingelesen." />}
        />
      )}

      {offen?.art === 'zuordnen' && (
        <Zuordnen
          api={api}
          projektId={projekt.id}
          importId={offen.importId}
          vorgabe={offen.vorgabe}
          schliessen={() => { setOffen(null); void laden(); setZaehler((n) => n + 1); }}
        />
      )}
      {offen?.art === 'befunde' && (
        <Befunddetails api={api} projektId={projekt.id} zeile={offen.zeile} schliessen={() => setOffen(null)} />
      )}
      {offen?.art === 'dokument' && (
        <Drawer offen titel={`Dokument für ${offen.zeile.typ}`} beschreibung={offen.zeile.familie} beiSchliessen={() => setOffen(null)}
          fuss={<button type="button" className="m-knopf" onClick={() => setOffen(null)}>Schließen</button>}>
          {(importe ?? []).length === 0
            ? <EmptyState klein titel="Noch kein Herstellerdokument eingelesen - oben hochladen." />
            : (
              <div className="lue-marken">
                {importe!.map((i) => (
                  <button key={i.id} type="button" className="m-knopf"
                    onClick={() => setOffen({ art: 'zuordnen', importId: i.id, vorgabe: { familie: offen.zeile.familie, typ: offen.zeile.typ, auswahl: null } })}>
                    {i.hersteller} {i.serie}
                  </button>
                ))}
              </div>
            )}
        </Drawer>
      )}
    </>
  );
}

// ═══ Schritt 1: Datei prüfen ═══════════════════════════════════════════════

function Hochladen({ api, neuLaden, auswaehlen }: { api: ApiClient; neuLaden: () => void; auswaehlen: (id: string) => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [pruefung, setPruefung] = useState<Importpruefung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const pruefen = async (datei: File) => {
    setLaeuft(true);
    setFehler(null);
    setPruefung(null);
    try {
      const p = await api.importPruefen(datei);
      setPruefung(p);
      if (p.gueltig) neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <ContentSection titel="Datei prüfen" beschreibung="Das Dokument wird geprüft und abgelegt; zugeordnet wird danach.">
      <Ablagefeld
        beschriftung="JSON hierher ziehen oder klicken"
        laeuft={laeuft}
        laeuftText="wird geprüft …"
        endung=".json"
        akzeptiert=".json,application/json"
        falsch="Nur JSON-Dateien im Format tuv-datasheet-import."
        ablegen={(d) => void pruefen(d)}
        fehlerMelden={setFehler}
      />
      {fehler && <ErrorState klein titel="Die Datei liess sich nicht prüfen." meldung={fehler} />}
      {pruefung && <Pruefergebnis pruefung={pruefung} auswaehlen={auswaehlen} />}
    </ContentSection>
  );
}

function Pruefergebnis({ pruefung: p, auswaehlen }: { pruefung: Importpruefung; auswaehlen: (id: string) => void }) {
  if (!p.gueltig) {
    return <ErrorState klein titel="Die Datei ist kein gültiges Importdokument." meldung={(p.fehler ?? []).join(' · ')} />;
  }

  return (
    <section className="lue-abschnitt">
      <PropertyGrid spalten={3} dicht eintraege={[
        { beschriftung: 'Hersteller', wert: p.hersteller ?? null },
        { beschriftung: 'Serie', wert: p.serie ?? null },
        { beschriftung: 'Baugrößen', wert: p.anzahlGroessen ?? 0 },
        { beschriftung: 'Betriebspunkte', wert: p.anzahlBetriebspunkte ?? 0 },
      ]} />

      {p.bereits && p.message && <p className="lue-leise"><StatusBadge klein zustand="info" text="schon eingelesen" /> {p.message}</p>}

      {/* Die Warnungen des Erzeugers gehören gelesen: Bei FK2-EU nennt die Überschrift „Breite 850–1500 mm“, die
          gedruckten Spalten beginnen aber bei 900. */}
      {(p.warnungen ?? []).length > 0 && (
        <div className="lue-hinweisliste">
          <StatusBadge klein zustand="warnung" text="Hinweise des Erzeugers" />
          <ul>{p.warnungen!.map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      )}
      {(p.hinweise ?? []).length > 0 && (
        <ul className="lue-hinweisliste">{p.hinweise!.map((h) => <li key={h}>{h}</li>)}</ul>
      )}
      {(p.unbekannteFelder ?? []).length > 0 && (
        <p className="lue-leise">
          Das Dokument nennt Felder, die es im Katalog nicht gibt: <strong>{p.unbekannteFelder!.join(', ')}</strong>. Sie
          werden übersprungen - anlegen lassen sie sich unter Einstellungen.
        </p>
      )}
      {(p.nichtAbbildbar ?? []).length > 0 && <p className="lue-leise">Ohne Zielfeld, laut Dokument: {p.nichtAbbildbar!.join(' · ')}</p>}

      {p.id && (
        <div className="lue-speichern">
          <button type="button" className="m-knopf m-knopf-haupt" onClick={() => auswaehlen(p.id!)}>Weiter zur Zuordnung</button>
        </div>
      )}
    </section>
  );
}

// ═══ Die Befunde eines Bauteiltyps ═══════════════════════════════════════════

/**
 * Ein Befund entsteht, wenn die geplante Luftmenge außerhalb der Herstellertabelle liegt; geladen wird erst auf Frage.
 * Gezeigt wird die Spanne: Liegen alle darüber, ist die Baugröße zu klein; darunter ist die Luftmenge falsch.
 */
function Befunddetails({ api, projektId, zeile, schliessen }: {
  api: ApiClient;
  projektId: string;
  zeile: Zuordnungszeile;
  schliessen: () => void;
}) {
  const [befunde, setBefunde] = useState<Bauteilbefund | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.bauteilbefunde(projektId, undefined, zeile.familie, zeile.typ)
      .then(setBefunde)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId, zeile.familie, zeile.typ]);

  const spalten: Spalte<Befundbauteil>[] = [
    {
      schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true, wert: (b) => b.aksSchluessel ?? elementkennung(b.elementGuid),
      inhalt: (b) => <span title={b.elementGuid}>{b.aksSchluessel ?? elementkennung(b.elementGuid)}</span>,
    },
    { schluessel: 'raum', titel: 'Raum', wert: (b) => b.raumnummer },
    { schluessel: 'ebene', titel: 'Ebene', wert: (b) => b.ebene },
    { schluessel: 'luftmenge', titel: 'Luftmenge', ...spalteFuer('volumenstrom_luft'), wert: (b) => b.volumenstrom },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', wert: (b) => (b.fehlerZustand === 'geprueft' ? 'angesehen' : 'offen'),
      inhalt: (b) => <StatusBadge klein zustand={b.fehlerZustand === 'geprueft' ? 'neutral' : 'warnung'} text={b.fehlerZustand === 'geprueft' ? 'angesehen' : 'offen'} />,
    },
  ];

  const mengen = (befunde?.bauteile ?? []).map((b) => b.volumenstrom).filter((m): m is number => m !== null);

  return (
    <Drawer offen titel={`Befunde ${zeile.typ}`} beschreibung={zeile.familie} beiSchliessen={schliessen} breite="720px"
      fuss={<button type="button" className="m-knopf" onClick={schliessen}>Schließen</button>}>
      {fehler && <ErrorState klein titel="Die Befunde konnten nicht geladen werden." meldung={fehler} />}
      {!befunde && !fehler && <LoadingState text="Befunde werden geladen …" zeilen={4} />}
      {befunde && (
        <>
          <p className="lue-leise">
            <StatusBadge klein zustand="warnung" text={`${formatiereZahl(befunde.gesamt, 0)} Bauteil(e) außerhalb der Herstellertabelle`} />
            {befunde.jeTyp[0]?.hinweis ? ` ${befunde.jeTyp[0].hinweis}` : ''}
          </p>
          {mengen.length > 0 && (
            <p className="lue-leise">
              Betroffene Luftmengen: {formatiereZahl(Math.min(...mengen), 0)} bis {formatiereZahl(Math.max(...mengen), 0)} m³/h.
              Das Produkt ist zugeordnet, die luftmengenabhängigen Werte nicht - sie ließen sich nur durch Extrapolation gewinnen.
            </p>
          )}
          <DataTable name="Bauteile mit Befund" spalten={spalten} zeilen={befunde.bauteile} schluessel={(b) => b.id} dicht />
          <p className="lue-leise">
            Zwei Ursachen sind möglich: Die Baugröße ist zu klein gewählt, oder die Luftmenge im Modell ist falsch. Beides
            gehört im Modell entschieden, nicht hier.
          </p>
        </>
      )}
    </Drawer>
  );
}
